<template>
	<div
	class="dragdrop flex flex-row items-start justify-start"
	v-if="game"
	@dragenter.prevent @dragover.prevent
	@drop.prevent.stop="removeChoice($event)">
		<table class="faa-table w-4/5">
			<!-- Column index label -->
			<tr class="faa-headers">
				<th class="b-n"></th>
				<th class="b-n"></th>
				<th 
				class="faa-header b-n bg-blue text-white text-center py-1"
				:class="{
					'rd-tl': (index == 0),
					'rd-tr': (index == (headers.length - 1)),
					'hidden': (headers.length < 2 && !header.content)
				}"
				v-for="(header, index) in headers" :key="header.column">
					<span>
						{{getColumnIndexLabel(index)}}
					</span>
				</th>
			</tr>

			<!-- Column titles -->
			<tr class="faa-headers">
				<th class="b-n"></th>
				<th class="b-n"></th>
				<th 
				class="faa-header bb-n bg-white text-center p-5"
				:class="{
					'opacity-0 b-n py-0': (headers.length < 2 && !header.content)
				}"
				v-for="header in headers" :key="header.column">
					<p v-if="header.content">
						{{header.content}}
					</p>
				</th>
			</tr>

			<!-- Content lines -->
			<tr class="faa-line"
			v-for="(row, index) in rows" :key="row.line">
				<!-- Line header index label -->
				<td
				class="faa-line-header b-n bg-blue text-white py-6 px-3"
				:class="{ 'rd-tl': (index == 0), 'rd-bl': (index == (rows.length - 1)) }">
					<span>
						{{index + 1}}
					</span>
				</td>

				<!-- Line header title -->
				<td class="faa-line-header br-n bg-white text-center py-6 px-8"
				:class="{ 'bb-n': (index == (rows.length - 1)) }">
					<span v-if="row.content">
						{{row.content}}
					</span>
				</td>

				<!-- Line answers cells -->
				<td
				class="faa-line-content"
				:class="{ 'bb-n': (index == (rows.length - 1)) }"
				v-for="(header, hIndex) in headers" :key="header.column"
				@dragenter.prevent @dragover.prevent
				@drop.prevent.stop="addChoice($event, row.line, hIndex)">

					<div 
					class="dragdrop-line-answers flex flex-wrap items-center 
					justify-center"
					v-if="answers[row.line] && answers[row.line][hIndex]">

						<div
						class="dragdrop-line-answer bg-white text-center rounded-full px-6 py-2 my-2 text-xs m-auto cursor-grab active:cursor-grabbing"
						:draggable="!showCorrection"
						v-for="(answer, index) in answers[row.line][hIndex]"
						:key="answer.id"
						:class="{
							'right-answer': (showCorrection && answer.right),
							'wrong-answer': (showCorrection && !answer.right),
							'cursor-not-allowed': showCorrection,
							'active:cursor-not-allowed': showCorrection,
						}"
						@dragstart="dragLabel($event, answer.id, { line: row.line, column: hIndex, index })">
							<span>
								{{labels[answer.id].text}}
							</span>
						</div>

					</div>

				</td>
			</tr>
		</table>

		<!-- Labels/Answers list -->
		<div class="dragdrop-labels flex flex-col w-1/5 pl-4">
			<div 
			class="dragdrop-label-row mx-auto"
			v-for="(label, index) in labels"
			:key="index">
				<div
				class="dragdrop-label bg-white text-center rounded-full px-6 py-2 my-3 text-xs cursor-grab active:cursor-grabbing"
				:draggable="!showCorrection"
				:class="{
					'cursor-not-allowed active:cursor-not-allowed': showCorrection,
					'opacity-0': label.used,
				}"
				@dragstart="dragLabel($event, label.id, { index })">
					<span>
						{{label.text}}
					</span>
				</div>
			</div>

		</div>
	</div>

</template>

<script>
	export default {
		name: 'DragDrop',
		props: {
			game: {
				type: Object,
				required: false,
				default: null
			},
			showCorrection: {
				type: Boolean,
				required: false,
				default: false
			}
		},
		data(){
			return {
				hasMultipleAnswers: false,
				answers: {},
			}
		},
		computed: {
			headers() {
				if (!this.game)
					return []

				return this.game.headers
				.filter((header) => (header.column != null))
				.sort((h1, h2) => (h1.column - h2.column))
			},
			rows() {
				if (!this.game)
					return []

				return this.game.headers
				.filter((header) => (header.line != null))
				.sort((h1, h2) => (h1.line - h2.line))
			},
			labels() {
				if (!this.game)
					return {}

				// Get all labels from questions answers
				const labels = this.game.questions.reduce((array, question) => {
					for (var i = 0; i < question.answers.length; i++) {
						let answer = question.answers[i]

						array.push({
							id: answer.id,
							text: answer.text,
							used: false,
							questionId: question.id,
							answerId: question.answers[i].id,
						})
					}

					return array
				}, [])

				// Shuffle labels array
				for(let i = labels.length - 1; i > 0; i--){
					const j = Math.floor(Math.random() * i)
					const temp = labels[i]

					labels[i] = labels[j]
					labels[j] = temp
				}

				// Reduce to a dictionary of answerId => label
				return labels.reduce((dict, label) => {					
					dict[label.answerId] = label

					return dict
				}, {})
			},
		},
		watch: {
			game: {
				handler() {
					// Reset states
					this.answers = {}
				},
				immediate: true,
			},
			labels: {
				handler() {
					this.answers = {}

					// Check if at least one question has multiple answers
					this.hasMultipleAnswers = false

					for (var i = 0; i < this.game.questions.length; i++) {
						if (this.game.questions[i].answers.length > 1) {
							this.hasMultipleAnswers = true
							break
						}
					}
				},
				immediate: true,
			},
			showCorrection: {
				handler(showCorrection) {
					if (!showCorrection || !this.game || !this.game.questions)
						return

					// Get all question right answers
					let rightAnswers = this.game.questions.reduce((dict, question) => {
						dict[question.id] = question.answers.map((a) => a.text)

						return dict
					}, {})

					// Check all question answers
					for (var i = 0; i < this.game.questions.length; i++) {
						const question = this.game.questions[i]

						const answers = (this.answers[question.line] && this.answers[question.line][question.column] ? this.answers[question.line][question.column] : [])

						// Check each filled answers
						for (var j = 0; j < answers.length; j++) {
							const label = this.labels[answers[j].id]
							const found = rightAnswers[question.id].indexOf(label.text)

							if (found > -1) {
								delete rightAnswers[question.id][found]
								this.$set(this.answers[question.line][question.column][j], 'right', true)
							} else {
								// Remove from current cell
								this.$delete(this.answers[question.line][question.column], j)

								label.used = false
							}
						}
					}

					// Add all remaining labels in their right cell but displayed as wrong answers
					const labelKeys = Object.keys(this.labels)
					for (var k = 0; k < labelKeys.length; k++) {
						const label = this.labels[labelKeys[k]]

						if (!label.used) {
							// Find label question
							const question = this.game.questions.find((question) => {
								const found = rightAnswers[question.id].indexOf(label.text)

								return (found > -1)
							})

							if (question) {
								const line = question.line
								const column = question.column

								// Remove from choice
								label.used = true

								// Remove from missing answers
								const found = rightAnswers[question.id].indexOf(label.text)
								delete rightAnswers[question.id][found]

								// Create answer arrays if needed
								if (!this.answers[line]) {
									this.$set(this.answers, line, {})
								}

								if (!this.answers[line][column]) {
									this.$set(this.answers[line], column, [])
								}

								// Add to answers array
								this.answers[line][column].push({ id: label.id })
							}
						}
					}
				},
				immediate: true,
			},
		},
		methods: {
			getColumnIndexLabel(index) {
				let label = ''

				const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
				for (var i = 0; i < Math.ceil((index + 1) / letters.length); i++) {
					label += letters[index % letters.length]
				}

				return label
			},
			dragLabel(e, id, origin) {
				e.dataTransfer.effectAllowed = 'move'
				e.dataTransfer.dropEffect = 'move'
				e.dataTransfer.setData('application/json', JSON.stringify({
					id,
					origin
				}))
			},
			addChoice(e, line, column) {
				// Get choice data
				let choice = null

				if (e.dataTransfer) {
					const json = e.dataTransfer.getData('application/json')

					try {
						choice = JSON.parse(json)
					} catch (error) {
						return
					}
				}

				if (choice && choice.id && choice.origin) {
					const { id, origin } = choice

					// Create answer arrays if needed
					if (!this.answers[line]) {
						this.$set(this.answers, line, {})
					}

					if (!this.answers[line][column]) {
						this.$set(this.answers[line], column, [])
					}

					// Add to answers array
					this.answers[line][column].push({ id })

					// Remove the choice from it's origin
					const oIndex = origin.index
					const oLine = origin.line
					const oColumn = origin.column

					if (oIndex !== undefined) {
						if (oLine !== undefined && oColumn !== undefined) {
							if (this.answers[oLine] && this.answers[oLine][oColumn]) {
								this.$delete(this.answers[oLine][oColumn], oIndex)
							}

							// Check if we need to swap the choice with another one
							if (!this.hasMultipleAnswers && this.answers[line][column].length > 1) {
								let swapAnswer = this.answers[line][column][0]
								this.answers[oLine][oColumn].push(swapAnswer)

								this.$delete(this.answers[line][column], 0)
							}
						} else {
							// Remove from possible choice
							this.labels[id].used = true

							// Check if we need to swap the choice with another one
							if (!this.hasMultipleAnswers && this.answers[line][column].length > 1) {
								let swapAnswer = this.answers[line][column][0]
								this.labels[swapAnswer.id].used = false

								this.$delete(this.answers[line][column], 0)
							}
						}
					}
				}
			},
			removeChoice(e) {
				// Get choice data
				let choice = null

				if (e.dataTransfer) {
					const json = e.dataTransfer.getData('application/json')

					try {
						choice = JSON.parse(json)
					} catch (error) {
						return
					}
				}

				if (choice && choice.id && choice.origin) {
					const { id, origin } = choice

					// Remove the choice from it's origin
					const oIndex = origin.index
					const oLine = origin.line
					const oColumn = origin.column

					if (oIndex !== undefined) {
						if (oLine !== undefined && oColumn !== undefined) {
							// Remove from answers array
							if (this.answers[oLine] && this.answers[oLine][oColumn]) {
								this.$delete(this.answers[oLine][oColumn], oIndex)
							}

							// "Add" to choice array
							this.labels[id].used = false
						}
					}
				}
			},
			getResults() {
				let results = {
					right: 0,
					wrong: 0,
					total: Object.keys(this.labels).length,
				}

				if (!this.showCorrection) {
					return results
				}			

				for (var i = 0; i < this.game.questions.length; i++) {
					const question = this.game.questions[i]

					const answers = (this.answers[question.line] && this.answers[question.line][question.column] ? this.answers[question.line][question.column] : [])

					// Check each filled answers
					for (var j = 0; j < answers.length; j++) {
						if (this.answers[question.line][question.column][j].right) {
							results.right += 1
						} else {
							results.wrong += 1
						}
					}
				}

				return results
			},
		},
	}
</script>

<style lang="scss" scoped>

/* Labels */
.dragdrop-label, .dragdrop-line-answer {
	@apply border border-white shadow select-none;
}

.dragdrop-line-answer.right-answer {
	@apply border border-green;
}

.dragdrop-line-answer.wrong-answer {
	@apply border border-red;
}

</style>
